/**
 * Lender-program validity, on its own so both the payload builders and the grid selectors can reach it.
 *
 * A leaf by design: it imports nothing. `isValidProgram` used to live in `paymentPayloadUtils`, which imports
 * the whole selector barrel - so a selector needing it created a cycle, and `check:circular` is a CI gate.
 * `paymentPayloadUtils` re-exports it, so every existing import keeps working.
 */

/**
 * Whether a program object carries everything Payment Services requires to price against it.
 *
 * Note `regionalId == null` rather than falsy: a real region 0 exists, and every other field is genuinely
 * required to be non-empty. An empty object passes none of these, which matters because the program cache
 * stores `{}` for a term it knows about but has no program for.
 */
export const isValidProgram = (program: any): boolean => {
    if (!program) {
        return false;
    }
    const { lenderId, branchId, programType, programName, regionalId, rateProgram } = program;
    if (!lenderId || !branchId || !programType || !programName || !rateProgram || regionalId == null) {
        return false;
    }
    return true;
};

export const isSameLenderProgram = (a: any, b: any): boolean =>
    isValidProgram(a) &&
    isValidProgram(b) &&
    a.lenderId === b.lenderId &&
    a.branchId === b.branchId &&
    a.programType === b.programType &&
    a.programName === b.programName &&
    a.regionalId === b.regionalId &&
    a.rateProgram === b.rateProgram;

export const isUserPricedProgram = (program: any): boolean => program?.programType === 'User' || program?.rateProgram === 'USER';

export type ProgramInfoSource = {
    lenderProgram?: Record<string, any>;
    creditTier?: number;
    creditTierDescription?: string;
    lenderLegalName?: string;
};

/**
 * Persist the lender program plus its credit-tier identity so the next payment override
 * sends `program.creditTier` / `program.creditTierDesc` to APS — without these, APS resolves
 * the tier from the global `creditScore` and may return a different lender tier than the
 * one the user applied (DE-equivalent of the "ALY Tier C → Tier A" multi-select bug).
 */
export const buildAppliedProgramInfo = (
    existing: Record<string, any> | undefined,
    source: ProgramInfoSource
): Record<string, any> => ({
    ...existing,
    ...source.lenderProgram,
    ...(source.creditTier != null && { creditTier: source.creditTier }),
    ...(source.creditTierDescription != null && { creditTierDesc: source.creditTierDescription }),
    ...(source.lenderLegalName != null && { lenderLegalName: source.lenderLegalName })
});
